import "moment-timezone";
import moment from "moment";
import Msg from "./message";
import Field from "./field";
import Entity from "./entity";
import { momentGetTimekey } from "../utils";
import IPeriodValue from "../interface/period-value";
import IConfigNumber from "../interface/field-cfg-number";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormula from "../interface/field-cfg-formula";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IFieldConfig from "../interface/field-cfg";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import SubstitutionOption from "../enum/substitution-option";
import Message from "../enum/message";
import LogicalValue from "../enum/logical-value";
import FormulaType from "../enum/formula-type";
import FieldType from "../enum/field-type";
import DateType from "../enum/date-type";
import DatePeriod from "../enum/date-period";
import CurrencyPosition from "../enum/currency-position";

class FormatInstance {

    /* Public Methods */

    public byPath(value: IDocumentValue, pathId: number[], option: SubstitutionOption = SubstitutionOption.None): string {
        return this.byFieldId(value, pathId[pathId.length - 1], option);
    }

    public byFieldId(value: IDocumentValue, fieldId: number, option: SubstitutionOption = SubstitutionOption.None): string {
        return this.byField(value, Field.get(fieldId), option);
    }

    public byField(value: IDocumentValue, field: IField | null, option: SubstitutionOption = SubstitutionOption.None): string {
        if (field) {
            const config: IFieldConfig = field.uiConfig;
            if (typeof value === 'string') {
                value = value.trim();
            }
            switch(field.uiType) {
                case FieldType.number:
                    return this.number(value, config as IConfigNumber);
                case FieldType.datetime:
                    return this.datetime(value as string, config as IConfigDatetime, option);
                case FieldType.check:
                    return this.check(value as string);
                case FieldType.formula:
                    return this.formula(value, config as IConfigFormula);
                case FieldType.lookup:
                    return this.byFieldId(value, field.foreignerFCNs[0]);
            }
        }
        switch(typeof value) {
            case 'number':
                return value.toString();
            case 'string':
                return value;
            default:
                return '';
        }
    }

    public document(doc: IDocument, entityId: number): IDocument {
        // TODO - Backbone delete - now used for report documents
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const result: IDocument = {
                id: doc.id
            };
            entity.fields.forEach((field: IField) => {
                if (field.uiType === FieldType.linked) {
                    result[`c${field.id}`] = doc[`d${field.id}`] || '';
                } else if (field.uiType !== FieldType.pointer && field.uiType !== FieldType.uid) {
                    result[`c${field.id}`] = this.byFieldId(doc[`c${field.id}`], field.id);
                }
            });
            return result;
        }
        return doc;
    }

    /* Private Methods */

    private datetime(value: string, config: IConfigDatetime, option: SubstitutionOption = SubstitutionOption.None): string {
        if (value) {
            if (config.type === DateType.DateTime && !value.includes('Z')) {
                value = value + 'Z';
            }
            let format: string = config.format;
            const date: moment.Moment = moment(value, moment.ISO_8601);
            if (config.timezone) {
                date.tz(config.timezone);
            }
            if (option === SubstitutionOption.DateOnly) {
                format = this.getDateOnlyFormat(format);
            } else if (option === SubstitutionOption.TimeOnly) {
                format = this.getTimeOnlyFormat(format);
            }
            return date.format(format);
        }
        return '';
    }

    private number(value: IDocumentValue, config: IConfigNumber): string {
        if (typeof value === 'string') {
            value = parseFloat(value);
        }
        if (typeof value === 'number' && !isNaN(value)) {
            const negative: boolean = value < 0;
            if (negative) {
                value = value * -1;
            }
            let result: string = value.toFixed(config.decimalLength);
            const decimal: string = result.split('.')[1] || '';
            let floor: string = result.split('.')[0];
            floor = floor.split('').reverse().join('');
            floor = (floor.match(/.{1,3}/g) || []).join(' ');
            floor = floor.split('').reverse().join('');
            if (config.decimalLength) {
                result = floor + config.decimalSeparator + decimal;
            } else {
                result = floor;
            }
            if (config.currencySymbol) {
                if (config.currencyPosition === CurrencyPosition.Left) {
                    result = `${config.currencySymbol} ${result}`;
                } else {
                    result = `${result} ${config.currencySymbol}`;
                }
            }
            if (negative) {
                result = '- ' + result;
            }
            return result;
        }
        return '';
    }

    private check(value: string): string {
        if (value) {
            if (value === LogicalValue.True) {
                return Msg.get(Message.checktrue);
            } else if (value === LogicalValue.False) {
                return Msg.get(Message.checkfalse);
            }
        }
        return Msg.get(Message.checkindetermine);
    }

    private formula(value: IDocumentValue, config: IConfigFormula): string {
        let result: string = '';
        switch(config.type) {
            case FormulaType.number:
                result = this.number(value, config.format as IConfigNumber);
                break;
            case FormulaType.datetime:
                result =  this.period(value, config.format as DatePeriod[]);
                break;
            case FormulaType.renewal:
                const formula: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
                if (formula.showPeriod) {
                    result =  this.period(value, config.format as DatePeriod[]);
                } else {
                    result =  this.datetime(value as string, config.format as IConfigDatetime);
                }
                break;
        }
        return result || Msg.get(Message.notavailable);
    }

    private period(val: IDocumentValue, format: DatePeriod[]): string {
        if (val) {
            const result: string[] = [];
            const value: IPeriodValue = val as IPeriodValue;
            Object.values(DatePeriod).forEach((period: string) => {
                const offset: number = value[period as keyof IPeriodValue] as number;
                if (offset) {
                    const timeKey: moment.RelativeTimeKey = momentGetTimekey(period as DatePeriod);
                    result.push(moment.localeData().relativeTime(offset, true, timeKey, false));
                }
            });
            if (result.length) {
                if (value.overdue) {
                    if (format.length > 1) {
                        return `${Msg.get(Message.overdue)} ${result.join(' ')}`;
                    } else {
                        return `-${result.join(' ')}`;
                    }
                }
                return result.join(' ');
            } else {
                const timeKey: moment.RelativeTimeKey = momentGetTimekey(format[format.length - 1]);
                return moment.localeData().relativeTime(0, true, timeKey, false);
            }
        }
        return '';
    }

    private getDateOnlyFormat(original: string): string {
        if (original.indexOf('H') > -1) {
            return original.substring(0, original.indexOf('H')).trim();
        } else if (original.indexOf('h') > -1) {
            return original.substring(0, original.indexOf('h')).trim();
        }
        return original;
    }

    private getTimeOnlyFormat(original: string): string {
        if (original.indexOf('H') > -1) {
            return original.substring(original.indexOf('H'), original.length).trim();
        } else if (original.indexOf('h') > -1) {
            return original.substring(original.indexOf('h'), original.length).trim();
        }
        return original;
    }
}

const Format = new FormatInstance();

export default Format;